import {
  Component,
  effect,
  inject,
  Injector,
  OnDestroy,
  signal,
  Signal,
  ViewChild,
} from '@angular/core';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';
import { BaseClass } from '../class/baseClass';
import { SidebarService } from '../../services/sidebar.service';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { LoginResponse } from '../../models/auth.model';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { InitialService } from '../../services/initial.service';
import { FormsModule } from '@angular/forms';
import { StorageService } from '../../services/StorageService.service';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { CommonModule } from '@angular/common';
import { notificationsService } from '../../services/notifications.service';
import {
  Notifications,
  NotificationsResponse,
} from '../../models/notification.model';
import { assets } from '../../core/constants/assets.const';
import { shouldSuppressNotificationSounds } from '../../core/utils/public-route.util';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { EncryptionService } from '../../services/encryption.service';
import { sideBar } from '../../core/constants/sidebar.const';
import { SocketsService } from '../../services/sockets.service';
import { ModuleConst } from '../../core/constants/keys.constant';
import { GlobalSearchService } from '../../services/globalSearch.service';
import { ListboxModule } from 'primeng/listbox';
import { InputSwitchModule } from 'primeng/inputswitch';
import { SidebarHandlingService } from '../../services/sidebarHandling.service';
import { DashboardService } from '../../services/dashboard.service';
import { HelpGuideService } from '../../services/helpGuide.service';
import { HelpGuide } from '../../models/help-guide.model';
import { RouterLink } from '@angular/router';
@Component({
  selector: 'logibids-header',
  standalone: true,
  imports: [
    MenuModule,
    ButtonModule,
    TranslateModule,
    AvatarModule,
    BadgeModule,
    MenuModule,
    DropdownModule,
    FormsModule,
    OverlayPanelModule,
    CommonModule,
    InfiniteScrollModule,
    ListboxModule,
    InputSwitchModule,
    RouterLink,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent extends BaseClass implements OnDestroy {
  isSidebarOpen: boolean = true;
  private subscriptionSubject$: Subscription;
  private destroy: Subject<boolean> = new Subject<boolean>();
  private socketListenersDestroy$ = new Subject<void>();
  profile = signal<LoginResponse | null>(null);
  private initialService = inject(InitialService);
  languages = this.initialService.languages;
  lang: string = '';
  storage!: Signal<string>;
  storageLang!: Signal<string>;
  sidebarData = signal<{
    parentId: number;
    subItemId: string | null;
    isSubMenu: false;
  } | null>(null);
  @ViewChild('notificationOp') notificationOverlay!: OverlayPanel;
  sidebarMultiModules: Array<string> = [
    '2.6',
    '3.1',
    '3.2',
    '3.3',
    '3.4',
    '3.5',
    '3.6',
    '3.8',
    '4.1',
    '4.2',
  ];
  protected size = signal<number>(10);
  protected from = signal<number>(1);
  protected total = signal<number>(0);
  notificationData = signal<Notifications[]>([]);
  assetsConst = assets;
  protected sideBar = sideBar;
  private searchService = inject(GlobalSearchService);
  results = this.searchService.results;
  searchValue = signal('');
  popularGuides = signal<HelpGuide[]>([]);
  constructor(
    private _authService: AuthService,
    public injector: Injector,
    private sidebarService: SidebarService,
    private authService: AuthService,
    private storageService: StorageService,
    private _notificationsService: notificationsService,
    private _EncryptionService: EncryptionService,
    private _SocketsService: SocketsService,
    private _SidebarHandlingService: SidebarHandlingService,
    private _DashboardService: DashboardService,
    private HelpGuideService: HelpGuideService
  ) {
    super(injector);
    this.sidebarService.sidebarOpen$
      .pipe(takeUntil(this.destroy))
      .subscribe(isOpen => {
        this.isSidebarOpen = isOpen;
      });
    this.subscriptionSubject$ = this._authService.profileDetails
      .pipe(takeUntil(this.destroy))
      .subscribe((res: LoginResponse | null) => {
        if (res !== null) {
          const normalized = this.normalizeProfile(res);
          this.profile.set(normalized);
          this.searchService.setProfileContext(normalized);
        } else {
          this.getProfile();
        }
      });
    this.storage = this.storageService.storage;
    this.storageLang = this.storageService.storageLang;
    this.lang = localStorage['language'];

    const cachedProfile =
      this._EncryptionService.decryptLocalStorage('profile');
    if (cachedProfile) {
      const normalized = this.normalizeProfile(cachedProfile);
      this.profile.set(normalized);
      this.searchService.setProfileContext(normalized);
    }

    effect(() => {
      this.sidebarData.set(JSON.parse(this.storage()));
      this.lang = this.storageLang();
    });
    this.getHelpGuide();
  }

  updateNotification(): void {
    this._notificationsService
      .viewNotification('viewed')
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        const current = this.profile();
        if (current) {
          this.profile.set({ ...current, notification_unread: 0 });
        }
        this.notificationData.update(notifications =>
          notifications.map(n => ({ ...n, viewed: true }))
        );
      });
  }

  onStatusChange(event: DropdownChangeEvent): void {
    if (event.value) {
      this.storageService.updateLangStorage(event.value);
    }
  }

  onAgentActiveToggle(event: { checked: boolean }): void {
    const previousActive = !event.checked;
    const current = this.profile();
    if (!current) {
      return;
    }

    this.profile.set({ ...current, active: event.checked });

    this._authService
      .updateProfile({ active: event.checked })
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: LoginResponse) => {
          const normalized = this.normalizeProfile({ ...current, ...res });
          this.profile.set(normalized);
          this._authService.profileDetails.next(normalized);
          this._EncryptionService.encryptLocalStorage(normalized, 'profile');
        },
        error: () => {
          this.profile.set({ ...current, active: previousActive });
        },
      });
  }

  private normalizeProfile(profile: LoginResponse): LoginResponse {
    return {
      ...profile,
      active: profile.active ?? true,
    };
  }

  getProfile(): void {
    this.authService
      .getProfile()
      .pipe(takeUntil(this.destroy))
      .subscribe((res: LoginResponse | null) => {
        if (res) {
          const normalized = this.normalizeProfile(res);
          this.profile.set(normalized);
          this.searchService.setProfileContext(normalized);
          this._authService.profileDetails.next(normalized);
          this._EncryptionService.encryptLocalStorage(normalized, 'profile');
          this._SocketsService.initSocket();
          this.socketListenersDestroy$.next();
          if (normalized.type !== 'SUB_ADMIN' && normalized.type !== 'AGENT') {
            this._SocketsService
              .notificationListner()
              .pipe(
                takeUntil(this.socketListenersDestroy$),
                takeUntil(this.destroy)
              )
              .subscribe(data => {
                if (data.type === 'DRIVER_ASSIGN') {
                  this.playDriverCrashSound();
                  this.toasterService.showWarning(data.message);
                } else {
                  this.playNotificationSound();
                  this.toasterService.showSuccess(
                    'New Service Provider Registered'
                  );
                }
              });
          }
          this.getAccessEnqueries();
        }
      });
  }

  getAccessEnqueries(): void {
    const current = this.profile();
    if (!current) {
      return;
    }
    const permissions = current.access_role?.permissions;

    const hasEnquiryAccess = permissions?.some(
      val => val.sidebar_module === ModuleConst.Enquiries
    );

    if (hasEnquiryAccess || current.type !== 'SUB_ADMIN') {
      this._SocketsService
        .notificationEnqueriesListner()
        .pipe(takeUntil(this.socketListenersDestroy$), takeUntil(this.destroy))
        .subscribe(data => {
          if (data) {
            this.toasterService.showSuccess(data.message);
            this.playNotificationSound();
          }
        });
    }
  }

  getHelpGuide(): void {
    this.HelpGuideService.getHelpCenterUserList('')
      .pipe(takeUntil(this.destroy))
      .subscribe((res: any) => {
        this.popularGuides.set(res.list || []);
      });
  }

  private playNotificationSound(): void {
    if (shouldSuppressNotificationSounds()) {
      return;
    }
    const audio = new Audio(assets.NotificationSound);
    audio.play().catch(error => {
      console.error('Error playing notification sound:', error);
    });
  }

  private playDriverCrashSound(): void {
    if (shouldSuppressNotificationSounds()) {
      return;
    }
    const audio = new Audio(assets.driverCrash);
    audio.play().catch(error => {
      console.error('Error playing notification sound:', error);
    });
  }

  getNewNotification(): void {
    this.from.set(1);
    this.getNotifications();
  }

  getNotifications(): void {
    const query = `from=${this.from()}&size=${this.size()}`;
    this._notificationsService
      .getNotifications(query)
      .pipe(takeUntil(this.destroy))
      .subscribe((res: NotificationsResponse) => {
        const newItems = res.list.map(item => ({
          ...item,
          viewed: this.isNotificationViewed(item.viewed),
        }));

        if (this.from() === 1) {
          this.notificationData.set(newItems);
        } else {
          this.notificationData.set([...this.notificationData(), ...newItems]);
        }

        this.total.set(res.total);
        this.from.set(this.from() + 1);
      });
  }

  onScrollingFinished(): void {
    if (this.notificationData().length >= this.total()) return;
    this.getNotifications();
  }

  sidebarOnOff(): void {
    this.sidebarService.toggleSidebar();
  }

  btn_redirect(list: Notifications): void {
    console.log(list);
    if (!list.viewed) {
      this.markNotificationAsViewed(list);
    }

    if (list.payload) {
      if (list.payload.type === 'ENQUIRY') {
        this.router.navigateByUrl('/status/enquiries');
        this.notificationOverlay.hide();
      }
      if (list.payload.type === 'VEHICLE') {
        this.router.navigateByUrl('/admin/service-provider');
        this.notificationOverlay.hide();
      }

      if (list.payload.type === 'DRIVER_ASSIGN') {
        const product: any = list.payload;
        const locations = (product.locations || []).map((loc: any) => ({
          lat: loc.lat,
          long: loc.long,
          address: loc.address,
          type: loc.type,
        }));

        this.router.navigate(['/status/driver-assigned'], {
          queryParams: {
            enquiryId: product.enquiry_id,
            spId: product?.sp_id || '',
            driverId: product?.driver_id || '',
            locations: JSON.stringify(locations),
          },
        });
        this.notificationOverlay.hide();
      }

      if (list.payload.chat_id && list.payload.type === 'AI_CHAT') {
        this.router.navigateByUrl(`/agent/chat-detail/${list.payload.chat_id}`);
        this.notificationOverlay.hide();
      }

      if (list.payload.type === 'REFERRAL') {
        this.router.navigateByUrl(`/referral/payout`);
        this.notificationOverlay.hide();
      }
    }
  }

  private markNotificationAsViewed(notification: Notifications): void {
    this._notificationsService
      .markNotificationAsViewed(notification.id)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: () => {
          this.notificationData.update(notifications =>
            notifications.map(item =>
              item.id === notification.id ? { ...item, viewed: true } : item
            )
          );

          const current = this.profile();
          if (current && current.notification_unread > 0) {
            this.profile.set({
              ...current,
              notification_unread: current.notification_unread - 1,
            });
          }
        },
      });
  }

  private isNotificationViewed(viewed: unknown): boolean {
    return viewed === true || viewed === 'true' || viewed === 1;
  }

  // Search

  onSearch(event: Event, op: OverlayPanel) {
    const value = (event.target as HTMLInputElement).value.trim();
    this.searchValue.set(value);

    if (!value) {
      op.hide();
      this.searchService.clear();
      return;
    }

    this.searchService.search(value);

    queueMicrotask(() => {
      this.results().length ? op.show(event) : op.hide();
    });
  }

  openIfHasData(event: Event, op: OverlayPanel) {
    if (!this.searchValue().trim()) return;
    if (this.results().length) {
      op.show(event);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navigate(item: any, op: OverlayPanel) {
    op.hide();

    this.searchValue.set('');
    this.searchService.clear();

    const hierarchy = this._SidebarHandlingService.getHierarchyByRoute(
      item.route,
      this.sideBar
    );

    if (hierarchy) {
      this.storageService.updateStorage(JSON.stringify(hierarchy));
      this._DashboardService.sidebarChange.next(true);
    }

    // Force input value reset (important)
    queueMicrotask(() => {
      this.router.navigateByUrl(item.route);
    });
  }

  ngOnDestroy(): void {
    this.socketListenersDestroy$.next();
    this.socketListenersDestroy$.complete();
    this.destroy.next(true);
    this.destroy.complete();
  }
}
